import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { hasPlayedToday, getTimeUntilNextGame, getWeeklyGameDays, formatDateForStorage, type WeeklyGameDay } from '../utils/dateUtils'
import { gameConfig } from '../config/gameConfig'
import './WelcomePage.css'

function WelcomePage() {
  const navigate = useNavigate()
  const [hasPlayed, setHasPlayed] = useState(false)
  const [timeUntilNext, setTimeUntilNext] = useState<{ hours: number; minutes: number; seconds: number } | null>(null)
  const [weeklyGames, setWeeklyGames] = useState<WeeklyGameDay[]>([])

  useEffect(() => {
    // Initial setup
    setHasPlayed(hasPlayedToday())
    setTimeUntilNext(getTimeUntilNextGame())
    setWeeklyGames(getWeeklyGameDays())
    
    // Use a ref to track the current date across interval calls
    const currentDateRef = { current: new Date().toDateString() }
    
    // Update countdown every second AND check for date changes
    const interval = setInterval(() => {
      const newTime = getTimeUntilNextGame()
      setTimeUntilNext(newTime)
      
      // Check for date changes (important for manual date changes on phone)
      const newDate = new Date().toDateString()
      if (newDate !== currentDateRef.current) {
        // Date has changed - clear old game data and update all states
        console.log('WelcomePage: Date change detected:', currentDateRef.current, '->', newDate)
        currentDateRef.current = newDate
        
        // Clear any old game state to ensure clean slate for new day (but preserve stats and completed games)
        // Note: We intentionally preserve 'xxxx-game-stats' to keep win/loss records
        
        // After clearing localStorage, update states
        setHasPlayed(hasPlayedToday())
        setTimeUntilNext(getTimeUntilNextGame())
        setWeeklyGames(getWeeklyGameDays())
      }
      
      // Also check if countdown reached zero (natural day transition)
      if (newTime.hours === 0 && newTime.minutes === 0 && newTime.seconds <= 1) {
        // Wait a moment for the new day to fully register, then check again
        setTimeout(() => {
          // Clear old game data for clean slate (but preserve stats and completed games)
          Object.keys(localStorage).forEach(key => {
            // Only clear in-progress game states, keep completed game results
            if (key.startsWith(`${gameConfig.dataSource}-game-state-`) || key === `${gameConfig.dataSource}-game-progress`) {
              localStorage.removeItem(key)
            }
            // Keep all game-YYYY-MM-DD keys as they contain completed game history
          })
          
          const updatedPlayStatus = hasPlayedToday()
          setHasPlayed(updatedPlayStatus)
          setTimeUntilNext(getTimeUntilNextGame())
          setWeeklyGames(getWeeklyGameDays())
        }, 2000) // Wait 2 seconds to ensure day transition is complete
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const handleDayButtonClick = (day: WeeklyGameDay) => {
    const localDateString = formatDateForStorage(day.date)
    if (day.status === 'unplayed') {
      // Navigate to play with specific date
      navigate(`/play?date=${localDateString}`)
    } else {
      // Show completed game result
      navigate(`/play?date=${localDateString}&review=true`)
    }
  }

  const getFacebookUrl = () => {
    switch (gameConfig.dataSource) {
      case 'roster':
        return "https://www.facebook.com/RosterGuess/"
      case 'running':
        return "https://www.facebook.com/5runs/"
      case 'soccer':
      default:
        return "https://www.facebook.com/PlayGuessFC/"
    }
  }

  const facebookUrl = getFacebookUrl()

  return (
    <div className="game-container">
      <div className="content-area">
        <div className="welcome-content">
          <div className="title-container">
            <p className="welcome-subtitle">
              The daily {gameConfig.dailyTitle} game
            </p>
          </div>

          <div className="play-button-container">
            {/* Today's game button - full width */}
            {weeklyGames.length > 0 && (
              <button
                className={`todays-game-button ${weeklyGames[weeklyGames.length - 1].status}`}
                onClick={() => handleDayButtonClick(weeklyGames[weeklyGames.length - 1])}
                title={`Game #${weeklyGames[weeklyGames.length - 1].gameNumber} - ${weeklyGames[weeklyGames.length - 1].date.toLocaleDateString()}`}
              >
                <div className="todays-game-content">
                  <div className="todays-game-text">
                    {weeklyGames[weeklyGames.length - 1].status === 'unplayed' ? 'PLAY TODAY\'S GAME' : 'VIEW TODAY\'S GAME'}
                  </div>
                  <div className="todays-game-indicator">
                    {weeklyGames[weeklyGames.length - 1].status === 'won' && '✅'}
                    {weeklyGames[weeklyGames.length - 1].status === 'lost' && '❌'}
                    {weeklyGames[weeklyGames.length - 1].status === 'unplayed' && gameConfig.theme.icon}
                  </div>
                </div>
              </button>
            )}
            
            {/* Previous 6 days in a row */}
            <div className="previous-games-container">
              <div className="previous-games-label">Catch up on previous days</div>
              <div className="weekly-game-selector">
                {weeklyGames.slice(0, -1).map((day) => (
                  <button
                    key={day.date.toISOString().split('T')[0]}
                    className={`day-button ${day.status}`}
                    onClick={() => handleDayButtonClick(day)}
                    title={`Game #${day.gameNumber} - ${day.date.toLocaleDateString()}`}
                  >
                    <div className="day-label">{day.dayLabel}</div>
                    <div className="day-indicator">
                      {day.status === 'won' && '✅'}
                      {day.status === 'lost' && '❌'}
                      {day.status === 'unplayed' && '⚫'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {hasPlayed && timeUntilNext && (
            <div className="countdown-container">
              <p className="countdown-text">
                Next game in: {timeUntilNext.hours.toString().padStart(2, '0')}:{timeUntilNext.minutes.toString().padStart(2, '0')}:{timeUntilNext.seconds.toString().padStart(2, '0')}
              </p>
            </div>
          )}

          <div className="game-info">
            <p className="welcome-subtitle">How to play</p>
            <div className="info-item">
              <span className="info-icon">🎯</span>
              <span>
                Guess letters to complete {gameConfig.thing} names.
              </span>
            </div>
            <div className="info-item">
              <span className="info-icon">{gameConfig.theme.icon}</span>
              <span>Win by revealing the common {gameConfig.commonThing}</span>
            </div>
          </div>

          <div className="social-links">
            <a 
              href={facebookUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="facebook-link"
            >
              Come say 👋 & follow us on Facebook
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WelcomePage
